/**
 * التقاط معرّف نقرة Snapchat (`ScCid`) وإيصاله إلى Shopify checkout.
 *
 * لماذا: أحداث Purchase تُرسَل من تطبيق Snapchat داخل Shopify (S2S + Web
 * Pixel)، لا من موقعنا. الزائر يصل إلى lecmo.com مع `?ScCid=...` لكنّ
 * المعرّف لا يعبر إلى checkout، فيفقد Purchase معرّف النقرة ويبلّغ Events
 * Manager عن «Low Click ID Return Rate». نحفظ المعرّف عند الوصول ثمّ
 * نمرّره بثلاث قنوات متوازية:
 *   1. كوكي طرف أوّل `_scclid` على الدومين الجذر — يقرأه بكسل Snap تلقائيّاً.
 *   2. سمتَا سلّة مخفيّتان `_sccid`/`_snap_click_at` → note_attributes.
 *   3. معامل `ScCid` في رابط checkout — يقرأه تطبيق Snapchat في Shopify.
 *
 * Consent: nothing is stored or forwarded without current explicit ads
 * consent. A first-landing click before any choice is kept in memory only;
 * an explicit grant persists it, rejection/withdrawal clears memory and
 * storage, and a withdrawn click is never revived afterwards.
 *
 * كلّ الدوالّ SSR-safe.
 */

import { hasAdsConsent, readConsent } from "@/lib/cookie-consent";
import { adsWithdrawnSince, currentConsentGeneration, onAdsWithdrawn } from "@/lib/consentTransitions";

/** Cart attribute keys owned by this bridge (refreshed/removed at checkout handoff). */
export const SNAP_CLICK_ATTR_KEYS: readonly string[] = ["_sccid", "_snap_click_at"];

const STORAGE_KEY = "lecmo:snap_click";
const COOKIE_NAME = "_scclid";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // ٧ أيّام — نافذة النقرة المعتمدة لدى Snap

export interface SnapClickId {
  sccid: string;
  /** ISO timestamp للنقرة */
  clickAt: string;
  expires: number;
}

function isValidSccid(value: string | null): value is string {
  if (!value) return false;
  const v = value.trim();
  return v.length > 0 && v.length <= 512;
}

/** يقرأ `ScCid` من سلسلة استعلام بلا حساسيّة لحالة الأحرف (ScCid/sccid/…). */
function readSccidParam(search: string): string | null {
  const params = new URLSearchParams(search);
  for (const [key, value] of params.entries()) {
    if (key.toLowerCase() === "sccid" && isValidSccid(value)) return value.trim();
  }
  return null;
}

/** يكتب كوكي `_scclid` على الدومين الجذر (lecmo.com) ليقرأه بكسل Snap وcheckout. */
function writeScclidCookie(sccid: string, expiresMs: number): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  try {
    const host = window.location.hostname;
    // على نطاقات المعاينة لا نضبط domain (كوكي host-only)؛ على الإنتاج نضبط
    // الجذر `.lecmo.com` ليصل الكوكي إلى أيّ نطاق فرعيّ يستضيف checkout.
    const domain = host === "lecmo.com" || host.endsWith(".lecmo.com") ? "; Domain=.lecmo.com" : "";
    const expires = new Date(expiresMs).toUTCString();
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(sccid)}; Expires=${expires}; Path=/; SameSite=Lax; Secure${domain}`;
  } catch {
    /* cookies blocked */
  }
}

function clearScclidCookie(): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  try {
    const host = window.location.hostname;
    const domain = host === "lecmo.com" || host.endsWith(".lecmo.com") ? "; Domain=.lecmo.com" : "";
    document.cookie = `${COOKIE_NAME}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax; Secure${domain}`;
  } catch {
    /* cookies blocked */
  }
}

/** In-memory click captured before the visitor's first consent choice. */
let pendingClick: (SnapClickId & { gen: number }) | null = null;

/** Clears all Snap-owned click state (memory + storage + cookie). */
export function clearSnapClickId(): void {
  pendingClick = null;
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
  clearScclidCookie();
}

// Synchronous withdrawal: clears memory even before delayed handlers run.
onAdsWithdrawn(clearSnapClickId);

/**
 * Call on every consent change: a grant persists a still-pending landing
 * click only if no withdrawal was recorded after it was captured; anything
 * else clears Snap-owned click state for good.
 */
export function syncSnapClickConsent(): void {
  if (typeof window === "undefined") return;
  if (!hasAdsConsent()) {
    clearSnapClickId();
    return;
  }
  const pending = pendingClick;
  pendingClick = null;
  if (pending && !adsWithdrawnSince(pending.gen) && pending.expires >= Date.now()) {
    const { gen: _gen, ...click } = pending;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(click));
    } catch {
      /* storage blocked */
    }
    writeScclidCookie(click.sccid, click.expires);
  }
}

/** يقرأ النقرة المحفوظة (أو null إن انتهت صلاحيّتها أو غابت الموافقة). */
export function getSnapClickId(): SnapClickId | null {
  if (typeof window === "undefined") return null;
  if (!hasAdsConsent()) {
    // Withdrawal (or rejection) invalidates stored attribution. Memory is
    // kept only while no choice has been made yet.
    if (readConsent()) pendingClick = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage blocked */
    }
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SnapClickId;
    if (!parsed?.expires || parsed.expires < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (!parsed.sccid) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * يقرأ الـURL الحاليّ ويحفظ `ScCid` إن وُجد.
 * نقرة أحدث تحلّ محلّ الأقدم (last-click).
 */
export function captureSnapClickId(search?: string, capturedGen?: number): SnapClickId | null {
  if (typeof window === "undefined") return null;
  const gen = capturedGen ?? currentConsentGeneration();
  // A withdrawal after the landing was observed: never resurrect its click.
  if (adsWithdrawnSince(gen)) return getSnapClickId();
  const consent = readConsent();
  if (consent && !consent.ads) {
    clearSnapClickId();
    return null;
  }
  try {
    const sccid = readSccidParam(search ?? window.location.search);
    if (!sccid) return getSnapClickId();
    const now = Date.now();
    const next: SnapClickId = {
      sccid,
      clickAt: new Date(now).toISOString(),
      expires: now + TTL_MS,
    };
    if (!consent) {
      // No choice yet: memory only, nothing written to advertising storage.
      pendingClick = { ...next, gen };
      return null;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    writeScclidCookie(sccid, next.expires);
    return next;
  } catch {
    return null;
  }
}

/**
 * سمات السلّة الخاصّة بـSnap (بادئة `_` = مخفيّة عن العميل في شوبيفاي).
 */
export function buildSnapClickAttributes(): Array<{ key: string; value: string }> {
  const attrs: Array<{ key: string; value: string }> = [];
  if (typeof window === "undefined" || !hasAdsConsent()) return attrs;
  const click = getSnapClickId();
  if (click) {
    attrs.push({ key: "_sccid", value: click.sccid });
    attrs.push({ key: "_snap_click_at", value: click.clickAt });
  }
  return attrs;
}
